import React, { useState, useEffect } from 'react';
import { InputTypes } from '@components/atoms/Input';
import { LabeledInput } from '@components/atoms/Input/LabeledInput';
import { Button, ButtonMode } from '@components/atoms/Button';
import { Typography } from '@components/atoms/Typography';
import { useTranslation } from 'react-i18next';
import { useRegistrationCarInfoStep } from '@services/useRegistrationCarInfoStep';
import { useData } from '@hooks/useData';
import { useOutletContext } from 'react-router-dom';
import { useNotifications } from '@hooks/useNotifications';
import { formatCarPlate, formatSeatCount, isValidPlate } from './../../../utils/validation';

interface CarInfoStepContext {
    onNext: () => void;
    onBack: () => void;
}

export interface CarInfoData {
    carBrand: string;
    carModel: string;
    carColor: string;
    carPlate: string;
    carSeatCount: string;
}

interface CarInfoDataErrors {
    carBrand: string[];
    carModel: string[];
    carColor: string[];
    carPlate: string[];
    carSeatCount: string[];
}

export interface CarInfoStepProps {
    data: CarInfoData;
    onChange: (field: keyof CarInfoData, value: string) => void;
    onNext: () => void;
    onBack?: () => void;
}

export const CarInfoStep: React.FC = () => {
    const { t } = useTranslation();
    const { carInfoStep, setCarInfoStep } = useData();
    const context = useOutletContext<CarInfoStepContext | null>();
    const onNext = context?.onNext || (() => {});
    const onBack = context?.onBack || (() => {});

    const { showDefaultNotification } = useNotifications();
    const { request: requestRegisterCarInfoStep, error, validationErrors, lastResponseTime } = useRegistrationCarInfoStep();
    const [errors, setErrors] = useState<CarInfoDataErrors>({
        carBrand: [],
        carModel: [],
        carColor: [],
        carPlate: [],
        carSeatCount: [],
    });

    const [localData, setLocalData] = React.useState<CarInfoData>({
        carBrand: carInfoStep?.data?.brand || '',
        carModel: carInfoStep?.data?.model || '',
        carColor: carInfoStep?.data?.color || '',
        carPlate: carInfoStep?.data?.plate || '',
        carSeatCount: carInfoStep?.data?.seats?.toString() || '',
    });

    useEffect(() => {
        if (carInfoStep?.data) {
            setLocalData({
                carBrand: carInfoStep.data.brand || '',
                carModel: carInfoStep.data.model || '',
                carColor: carInfoStep.data.color || '',
                carPlate: carInfoStep.data.plate || '',
                carSeatCount: carInfoStep.data.seats?.toString() || '',
            });
        }
    }, [carInfoStep?.data]);

    useEffect(() => {
        if (lastResponseTime) {
            if (validationErrors) {
                setErrors({
                    carBrand: validationErrors['brand'] || [],
                    carModel: validationErrors['model'] || [],
                    carColor: validationErrors['color'] || [],
                    carPlate: validationErrors['plate'] || [],
                    carSeatCount: validationErrors['seats'] || [],
                });
            } else if (error) {
                showDefaultNotification({ type: 'error', message: error });
            } else {
                onNext();
            }
        }
    }, [lastResponseTime]);

    const translations = {
        title: t('car-info-step.title'),
        brand: t('car-info-step.brand'),
        model: t('car-info-step.model'),
        color: t('car-info-step.color'),
        plate: t('car-info-step.plate'),
        seats: t('car-info-step.seats'),
        brandRequiredError: t('car-info-step.brand.required-error'),
        modelRequiredError: t('car-info-step.model.required-error'),
        colorRequiredError: t('car-info-step.color.required-error'),
        plateRequiredError: t('car-info-step.plate.required-error'),
        plateInvalidError: t('car-info-step.plate.invalid-error'),
        seatsRequiredError: t('car-info-step.seats.required-error'),
        seatsMinError: t('car-info-step.seats.min-error'),
        prev: t('navigation.prev'),
        next: t('navigation.next'),
    };

    const handleChange = (field: keyof CarInfoData, value: string) => {
        const updated = { ...localData, [field]: value };
        setLocalData(updated);

        setCarInfoStep?.({
            ...carInfoStep,
            data: {
                uuid: carInfoStep?.data?.uuid || '',
                brand: field === 'carBrand' ? value : carInfoStep?.data?.brand || '',
                model: field === 'carModel' ? value : carInfoStep?.data?.model || '',
                color: field === 'carColor' ? value : carInfoStep?.data?.color || '',
                plate: field === 'carPlate' ? value : carInfoStep?.data?.plate || '',
                seats: field === 'carSeatCount' ? Number(value) : carInfoStep?.data?.seats || 0,
            },
            success: carInfoStep?.success ?? true,
            message: carInfoStep?.message ?? '',
        });
    };

    const validateForm = (): boolean => {
        const seatCount = Number(localData.carSeatCount);

        const newErrors: CarInfoDataErrors = {
            carBrand: localData.carBrand ? [] : [translations.brandRequiredError],
            carModel: localData.carModel ? [] : [translations.modelRequiredError],
            carColor: [],
            carPlate: localData.carPlate
                ? isValidPlate(localData.carPlate)
                    ? []
                    : [translations.plateInvalidError]
                : [translations.plateRequiredError],
            carSeatCount:
                !localData.carSeatCount || isNaN(seatCount)
                    ? [translations.seatsRequiredError]
                    : seatCount < 1
                      ? [translations.seatsMinError]
                      : [],
        };
        setErrors(newErrors);
        const hasErrors = Object.values(newErrors).some((fieldErrors) => fieldErrors.length > 0);

        return hasErrors ? false : true;
    };

    const submitForm = async () => {
        if (!validateForm()) return;

        requestRegisterCarInfoStep({
            brand: localData.carBrand,
            model: localData.carModel,
            color: localData.carColor,
            plate: localData.carPlate,
            seats: Number(localData.carSeatCount),
        });
    };

    return (
        <div className="d-flex flex-column gap-2">
            <Typography align="center" fontSize="16" fontWeight="400" color="muted">
                {translations.title}
            </Typography>

            <LabeledInput
                type={InputTypes.text}
                label={`${translations.brand}*`}
                value={localData.carBrand}
                onChange={(value) => handleChange('carBrand', value)}
                errorMessages={errors.carBrand}
            />
            <LabeledInput
                type={InputTypes.text}
                label={`${translations.model}*`}
                value={localData.carModel}
                onChange={(value) => handleChange('carModel', value)}
                errorMessages={errors.carModel}
            />
            <LabeledInput
                type={InputTypes.text}
                label={`${translations.color}`}
                value={localData.carColor}
                onChange={(value) => handleChange('carColor', value)}
                errorMessages={errors.carColor}
            />
            <LabeledInput
                type={InputTypes.text}
                label={`${translations.plate}*`}
                value={localData.carPlate}
                maxLength={8}
                onChange={(value) => handleChange('carPlate', formatCarPlate(value))}
                placeholder="XX-XX-XX"
                errorMessages={errors.carPlate}
            />
            <LabeledInput
                type={InputTypes.number}
                label={`${translations.seats}*`}
                value={localData.carSeatCount}
                onChange={(value) => handleChange('carSeatCount', formatSeatCount(value))}
                errorMessages={errors.carSeatCount}
                min={0}
                max={50}
            />

            <Button onClick={onBack} label={translations.prev} mode={ButtonMode.primary} outline />
            <Button label={translations.next} mode={ButtonMode.primary} onClick={submitForm} />
        </div>
    );
};
